import { useEffect, useState } from "react";
import api, { fmtDate, fmtIDR, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardCheck, Boxes, Wallet, TrendingUp, Package, AlertTriangle } from "lucide-react";

const Card = ({ icon: Icon, label, value, tone = "bg-slate-100 text-slate-600", grad }) => (
  <div className={`rounded-xl border shadow-sm p-4 ${grad ? "text-white border-transparent " + grad : "bg-white border-slate-200"}`} data-testid={`stock-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
    <div className={`flex items-center gap-2 text-xs uppercase tracking-wide font-medium ${grad ? "opacity-90" : "text-slate-500"}`}>
      {!grad && <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${tone}`}><Icon className="w-4 h-4" /></span>}
      {grad && <Icon className="w-4 h-4" />} {label}
    </div>
    <div className={`text-xl font-semibold font-mono-num mt-2 ${grad ? "" : "text-slate-900"}`}>{value}</div>
  </div>
);

export default function Stock() {
  const { isPriv, isOwner } = useAuth();
  const [moves, setMoves] = useState([]);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [phys, setPhys] = useState({});   // {pid: {qty, imei, note}}
  const activeBranch = localStorage.getItem("branch_id") || "all";

  const load = () => {
    setLoading(true);
    api.get("/stock/movements").then(({ data }) => setMoves(data)).finally(() => setLoading(false));
    api.get("/stock/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data));
  };
  useEffect(() => { load(); api.get("/branches").then(({ data }) => setBranches(data)); }, []);

  const opnameBranchName = isOwner
    ? (activeBranch === "all" ? "Cabang aktif (pilih 1 cabang di atas)" : branches.find((b) => b.id === activeBranch)?.name || "-")
    : branches.find((b) => b.id === activeBranch)?.name || "Cabang Anda";
  const opnameDisabled = isOwner && activeBranch === "all";

  const saveOpname = async () => {
    const items = products.filter((p) => phys[p.id]?.qty !== undefined && phys[p.id]?.qty !== "").map((p) => ({
      product_id: p.id, product_name: p.name, system_stock: p.stock, physical_stock: Number(phys[p.id].qty),
      imei: phys[p.id].imei || "", note: phys[p.id].note || "",
    }));
    if (items.length === 0) return toast.error("Isi minimal 1 stok fisik");
    await api.post("/stock/opname", { note: `Opname ${opnameBranchName}`, items });
    toast.success("Opname tersimpan"); setOpen(false); setPhys({}); load();
  };

  const KIND = { sale: "Penjualan", purchase: "Pembelian", opname: "Opname", trade_in: "Tukar Tambah", cancel_sale: "Batal Jual", cancel_purchase: "Batal Beli", service: "Servis", adjust: "Penyesuaian" };
  const s = summary || {};
  const hp = s.hp || {}; const acc = s.accessories || {};

  const CatPanel = ({ title, d, grad }) => (
    <div className={`rounded-xl shadow-sm p-4 text-white ${grad}`}>
      <div className="font-medium mb-2">{title}</div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div><div className="text-[10px] uppercase opacity-80">Unit</div><div className="text-lg font-semibold font-mono-num">{d.total_units ?? 0}</div></div>
        <div><div className="text-[10px] uppercase opacity-80">Modal</div><div className="text-sm font-semibold font-mono-num">{isPriv ? fmtIDR(d.total_modal) : "•••"}</div></div>
        <div><div className="text-[10px] uppercase opacity-80">Potensi Laba</div><div className="text-sm font-semibold font-mono-num">{isPriv ? fmtIDR(d.potensi_laba) : "•••"}</div></div>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Stok & Opname" subtitle="Nilai stok, lokasi stok per cabang, mutasi & opname">
        <Button onClick={() => setOpen(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="opname-btn"><ClipboardCheck className="w-4 h-4 mr-1.5" /> Stock Opname</Button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div data-testid="stock-panel-hp"><CatPanel title="📱 Unit HP" d={hp} grad="bg-gradient-to-br from-sky-600 to-sky-700" /></div>
        <div data-testid="stock-panel-acc"><CatPanel title="🎧 Aksesoris / Lainnya" d={acc} grad="bg-gradient-to-br from-violet-600 to-violet-700" /></div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card icon={Package} label="Total SKU" value={s.total_products ?? 0} tone="bg-slate-100 text-slate-600" />
        <Card icon={Boxes} label="Total Unit" value={s.total_units ?? 0} tone="bg-sky-50 text-sky-600" />
        <Card icon={Wallet} label="Total Modal Stok" value={isPriv ? fmtIDR(s.total_modal) : "•••"} grad="bg-gradient-to-br from-slate-700 to-slate-800" />
        <Card icon={TrendingUp} label="Nilai Jual (Omset Potensial)" value={fmtIDR(s.total_nilai_jual)} grad="bg-gradient-to-br from-sky-600 to-sky-700" />
        <Card icon={AlertTriangle} label="Stok Menipis" value={s.low_stock_count ?? 0} tone="bg-amber-50 text-amber-600" />
        <Card icon={AlertTriangle} label="Stok Habis" value={s.out_of_stock ?? 0} tone="bg-red-50 text-red-600" />
      </div>

      {/* Lokasi Stok per Cabang */}
      <h3 className="font-medium text-slate-800 mb-3">Lokasi Stok per Cabang <span className="text-xs text-slate-400">— "HP ini stoknya di mana?"</span></h3>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {["Produk", "Total", ...branches.map((b) => b.name.replace("Cabang ", "")), "Status"].map((h, i) => <th key={i} className="px-4 py-3 text-left font-medium text-slate-500 whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={branches.length + 3}><Loading /></td></tr>
              : products.length === 0 ? <tr><td colSpan={branches.length + 3}><Empty /></td></tr>
              : products.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`stockloc-${p.id}`}>
                  <td className="px-4 py-3"><div className="font-medium text-slate-800">{p.name}</div><div className="text-[11px] text-slate-400 font-mono-num">{p.imei || p.sku || ""}</div></td>
                  <td className="px-4 py-3 font-mono-num font-semibold">{p.total_stock}</td>
                  {branches.map((b) => {
                    const bb = (p.branch_breakdown || []).find((x) => x.branch_id === b.id);
                    const configured = bb && bb.configured;
                    const st = bb?.stock ?? 0;
                    return <td key={b.id} className={`px-4 py-3 font-mono-num ${!configured ? "text-amber-500" : st <= 0 ? "text-slate-300" : st <= (bb?.min_stock || 0) ? "text-red-600 font-medium" : "text-slate-700"}`} title={!configured ? "Belum dikonfigurasi (stok 0)" : ""}>📍 {st}{!configured && <AlertTriangle className="w-3 h-3 inline ml-0.5 text-amber-500" />}</td>;
                  })}
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${p.total_stock > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{p.total_stock > 0 ? "Tersedia" : "Habis"}</span></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <h3 className="font-medium text-slate-800 mb-3">Mutasi Stok Terakhir</h3>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {["Tanggal", "Produk", "Jenis", "Perubahan", "Referensi"].map((h) => <th key={h} className="px-4 py-3 text-left font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={5}><Loading /></td></tr>
              : moves.length === 0 ? <tr><td colSpan={5}><Empty /></td></tr>
              : moves.map((m) => (
                <tr key={m.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500">{fmtDate(m.date)}</td>
                  <td className="px-4 py-3">{m.product_name}</td>
                  <td className="px-4 py-3">{KIND[m.kind] || m.kind}</td>
                  <td className={`px-4 py-3 font-mono-num font-medium ${m.delta >= 0 ? "text-green-600" : "text-red-600"}`}>{m.delta > 0 ? "+" : ""}{m.delta}</td>
                  <td className="px-4 py-3 font-mono-num text-xs text-slate-400">{m.reference || m.ref}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Stock Opname — {opnameBranchName}</DialogTitle></DialogHeader>
          {opnameDisabled ? (
            <div className="py-8 text-center text-amber-600 text-sm">Pilih salah satu cabang (bukan "Semua Cabang") di header untuk melakukan opname.</div>
          ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0"><tr>
                {["Foto", "Produk", "Brand", "SKU", "Sistem", "Fisik", "Selisih", "IMEI", "Keterangan"].map((h) => <th key={h} className="px-3 py-2 text-left text-slate-500 font-medium">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {products.map((p) => {
                  const row = phys[p.id] || {}; const v = row.qty;
                  const diff = v === undefined || v === "" ? null : Number(v) - p.stock;
                  const set = (patch) => setPhys({ ...phys, [p.id]: { ...row, ...patch } });
                  return (
                    <tr key={p.id}>
                      <td className="px-3 py-2">{p.image_url ? <img src={fileUrl(p.image_url)} alt="" className="w-8 h-8 rounded object-cover border border-slate-200" /> : <div className="w-8 h-8 rounded bg-slate-100" />}</td>
                      <td className="px-3 py-2">{p.name}</td>
                      <td className="px-3 py-2 text-slate-500">{p.brand || "-"}</td>
                      <td className="px-3 py-2 font-mono-num text-xs text-slate-500">{p.sku || "-"}</td>
                      <td className="px-3 py-2 font-mono-num">{p.stock}</td>
                      <td className="px-3 py-2"><Input type="number" value={v ?? ""} onChange={(e) => set({ qty: e.target.value })} className="h-8 w-20" data-testid={`opname-qty-${p.id}`} /></td>
                      <td className={`px-3 py-2 font-mono-num ${diff === null ? "text-slate-300" : diff === 0 ? "text-slate-400" : diff > 0 ? "text-green-600" : "text-red-600"}`}>{diff === null ? "-" : diff}</td>
                      <td className="px-3 py-2"><Input value={row.imei ?? ""} onChange={(e) => set({ imei: e.target.value })} placeholder="IMEI" className="h-8 w-36" data-testid={`opname-imei-${p.id}`} /></td>
                      <td className="px-3 py-2"><Input value={row.note ?? ""} onChange={(e) => set({ note: e.target.value })} placeholder="Keterangan" className="h-8 w-40" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={saveOpname} disabled={opnameDisabled} className="bg-sky-600 hover:bg-sky-700" data-testid="opname-save-btn">Simpan Opname</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
